import type { Alert, LineHealth } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { formatIsoTime } from "../app/format";
import { useIsMobile } from "../app/useIsMobile";
import { useNetworkId } from "../config/ConfigProvider";
import { useActivePostParam } from "../posts/PostProvider";
import { usePolledResource } from "../cockpit/hooks";
import { LineBadge } from "../cockpit/bits";
import { useAlerts } from "../map/useMapData";

/**
 * Santé des lignes + alertes réseau GTFS-RT, en LECTURE SEULE (F2.4, ex-colonne
 * latérale de la carte pilote — déplacée ici, P2-04). Aucune création par le
 * régulateur (analyse-maquettes §7, A7).
 */
export function NetworkHealthView() {
  const backend = useBackend();
  const networkId = useNetworkId();
  const postParam = useActivePostParam();
  const isMobile = useIsMobile();

  const health = usePolledResource(
    (signal) => backend.api.getLinesHealth(networkId, postParam ? { post: postParam } : {}, signal),
    [backend, networkId, postParam],
    15_000,
  );
  const { alerts } = useAlerts();

  return (
    <div
      style={{
        display: "grid",
        gap: 24,
        // Mobile (2026-09-11) : empilé plutôt qu'une colonne de 380px qui ne
        // laisse presque rien à l'autre sur un téléphone.
        gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 380px) minmax(0, 1fr)",
        alignItems: "start",
      }}
    >
      <section>
        <h2 className="ovl" style={{ marginBottom: 10 }}>
          Santé des lignes
        </h2>
        <ul style={{ display: "grid", gap: 6 }}>
          {(health.data ?? []).map((line) => (
            <LineHealthRow key={line.lineId} line={line} />
          ))}
        </ul>
      </section>

      <section>
        <h2 className="ovl" style={{ marginBottom: 10 }}>
          Alertes réseau <span className="has-text-grey">· lecture seule</span>
        </h2>
        {alerts.length === 0 ? (
          <p className="has-text-grey is-size-7">Aucune alerte en cours.</p>
        ) : (
          <ul style={{ display: "grid", gap: 8 }}>
            {alerts.map((alert) => (
              <AlertRow key={alert.id} alert={alert} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function LineHealthRow({ line }: { line: LineHealth }) {
  const tone = line.status === "down" ? "var(--sentinelle-signal)" : line.status === "degraded" ? "#b45309" : "var(--sentinelle-ok)";
  const label = line.status === "down" ? "en panne" : line.status === "degraded" ? "dégradée" : "nominale";
  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        border: "1px solid var(--bulma-border-weak)",
        borderRadius: 3,
      }}
    >
      <LineBadge lineId={line.lineId} size={20} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem" }}>
          <span className="ovl" style={{ color: tone }}>
            {label}
          </span>
          {line.openAlerts > 0 ? (
            <span className="mono" style={{ color: "var(--sentinelle-signal)" }}>
              {line.openAlerts} alerte{line.openAlerts > 1 ? "s" : ""}
            </span>
          ) : null}
        </span>
        {line.scheduleFallback ? (
          <span className="ovl" style={{ color: "var(--sentinelle-signal)", display: "block", marginTop: 3 }}>
            horaire théorique{line.since ? ` depuis ${formatIsoTime(line.since)}` : ""}
          </span>
        ) : null}
      </span>
    </li>
  );
}

function AlertRow({ alert }: { alert: Alert }) {
  const tone =
    alert.severity === "severe" ? "var(--sentinelle-signal)" : alert.severity === "warning" ? "#b45309" : "var(--sentinelle-ink-3)";
  return (
    <li style={{ borderLeft: `3px solid ${tone}`, padding: "6px 9px", background: "var(--bulma-scheme-main-bis)" }}>
      <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--sentinelle-ink)" }}>{alert.header}</div>
      <div className="ovl" style={{ margin: "3px 0" }}>
        {alert.activePeriod.start ? formatIsoTime(alert.activePeriod.start) : "—"}
        {alert.activePeriod.end ? ` → ${formatIsoTime(alert.activePeriod.end)}` : ""}
      </div>
      <div style={{ fontSize: "0.72rem", color: "var(--sentinelle-ink-2)" }}>{alert.description}</div>
    </li>
  );
}
